import { DEFAULT_TAX_RATE, getTradeTaxRate } from "../constants/tax";
import { roundTo2, roundTo2Str } from "./numberUtils";
import {
  getAgentOfficeExpenseAmounts,
  sumAgentOfficeExpenses,
  sumAgentOfficeExpensesByColumn,
} from "./officeExpenses";

export { roundTo2, roundTo2Str };

function isReferralAgentRow(a) {
  return (a.classification || "").toUpperCase() === "REFERRAL";
}

/** Accepts "yes", "Yes", true (some saves/API paths differ). */
export function isBuyerRebateIncludedYes(a) {
  const v = a?.buyerRebateIncluded;
  if (v === true || v === "yes") return true;
  if (typeof v === "string" && v.toLowerCase() === "yes") return true;
  return false;
}

/**
 * Row belongs to agent double ender commission (listing/selling pair), even if
 * `classification` was not persisted on the subdocument.
 */
function isAgentDoubleEnderCommissionRow(a) {
  if ((a.classification || "").toUpperCase() === "AGENT DOUBLE ENDER")
    return true;
  const tt = (a.transactionType || "").toUpperCase();
  return tt === "LISTING" || tt === "SELLING";
}

/**
 * Total buyer rebate for ledger/UI. For AGENT DOUBLE ENDER, counts each agent once
 * (rebate is stored on the SELLING row; avoids doubling if both rows had values).
 */
export function getTotalBuyerRebateAmount(trade) {
  if (!trade?.agentCommissionList?.length) return 0;
  const classification = trade.keyInfo?.classification || "";
  if (classification !== "AGENT DOUBLE ENDER") {
    return roundTo2(
      trade.agentCommissionList.reduce((t, a) => {
        if (isReferralAgentRow(a)) return t;
        if (isBuyerRebateIncludedYes(a) && a.buyerRebateAmount) {
          return t + parseFloat(a.buyerRebateAmount || 0);
        }
        return t;
      }, 0),
    );
  }
  const byAgent = new Map();
  for (const a of trade.agentCommissionList) {
    if (isReferralAgentRow(a)) continue;
    if (!isAgentDoubleEnderCommissionRow(a)) continue;
    if (!isBuyerRebateIncludedYes(a) || !a.buyerRebateAmount) continue;
    const key = String(a.agentId ?? a.agentName ?? "");
    const amt = parseFloat(a.buyerRebateAmount || 0) || 0;
    const isSelling = (a.transactionType || "").toUpperCase() === "SELLING";
    if (!byAgent.has(key)) {
      byAgent.set(key, { amt, isSelling });
    } else if (isSelling) {
      byAgent.set(key, { amt, isSelling: true });
    }
  }
  let total = 0;
  byAgent.forEach(({ amt }) => {
    total += amt;
  });
  for (const a of trade.agentCommissionList) {
    if (isReferralAgentRow(a)) continue;
    if (isAgentDoubleEnderCommissionRow(a)) continue;
    if (isBuyerRebateIncludedYes(a) && a.buyerRebateAmount) {
      total += parseFloat(a.buyerRebateAmount || 0);
    }
  }
  return roundTo2(total);
}

const BUYER_REBATE_LEDGER_ACCOUNT = {
  accountNumber: "52100",
  accountName: "Referral Fees",
};

/**
 * One entry per agent with a buyer rebate, for separate ledger credits (52100).
 * Mirrors {@link getTotalBuyerRebateAmount} so totals stay consistent.
 * @param {Object} trade
 * @returns {{ agentName: string, amount: number }[]}
 */
export function getPerAgentBuyerRebatesForLedger(trade) {
  if (!trade?.agentCommissionList?.length) return [];
  const classification = trade.keyInfo?.classification || "";
  const list = trade.agentCommissionList;

  if (classification === "AGENT DOUBLE ENDER") {
    const byAgent = new Map();
    for (const a of list) {
      if (isReferralAgentRow(a)) continue;
      if (!isAgentDoubleEnderCommissionRow(a)) continue;
      if (!isBuyerRebateIncludedYes(a) || !a.buyerRebateAmount) continue;
      const key = String(a.agentId ?? a.agentName ?? "");
      const amt = parseFloat(a.buyerRebateAmount || 0) || 0;
      if (amt <= 0) continue;
      const isSelling = (a.transactionType || "").toUpperCase() === "SELLING";
      const name = (a.agentName || "").trim() || key;
      if (!byAgent.has(key)) {
        byAgent.set(key, { amt, isSelling, name });
      } else if (isSelling) {
        const prev = byAgent.get(key);
        byAgent.set(key, { amt, isSelling: true, name: prev.name || name });
      }
    }
    const out = [];
    byAgent.forEach(({ amt, name }) => {
      out.push({ agentName: name, amount: roundTo2(amt) });
    });
    for (const a of list) {
      if (isReferralAgentRow(a)) continue;
      if (isAgentDoubleEnderCommissionRow(a)) continue;
      if (!isBuyerRebateIncludedYes(a) || !a.buyerRebateAmount) continue;
      const amt = parseFloat(a.buyerRebateAmount || 0) || 0;
      if (amt <= 0) continue;
      out.push({
        agentName: (a.agentName || "").trim() || String(a.agentId ?? ""),
        amount: roundTo2(amt),
      });
    }
    return out;
  }

  const out = [];
  for (const a of list) {
    if (isReferralAgentRow(a)) continue;
    if (!isBuyerRebateIncludedYes(a) || !a.buyerRebateAmount) continue;
    const amt = parseFloat(a.buyerRebateAmount || 0) || 0;
    if (amt <= 0) continue;
    out.push({
      agentName: (a.agentName || "").trim() || String(a.agentId ?? ""),
      amount: roundTo2(amt),
    });
  }
  return out;
}

/** Account used for buyer rebate credits (per chart of accounts 52100). */
export function getBuyerRebateLedgerAccount() {
  return { ...BUYER_REBATE_LEDGER_ACCOUNT };
}

/**
 * Buyer rebate for the Financial sheet Liabilities row (Listing vs Selling columns).
 * For AGENT DOUBLE ENDER, each agent's rebate is counted once and placed in the column
 * for the transaction row that holds it (SELLING row wins when both exist), matching
 * {@link getTotalBuyerRebateAmount}.
 * When `agentKeys` is a Set, only those agents (String(agentId ?? agentName)) are included
 * — used when printing a single agent's sheet so the rebate is not the trade total.
 * @param {Object} trade
 * @param {{ agentKeys?: Set<string> | null }} [options]
 * @returns {{ listing: number, selling: number }}
 */
export function getBuyerRebateListingSellingForSheet(trade, options = {}) {
  const { agentKeys } = options;
  const classification = trade.keyInfo?.classification || "";
  const list = trade.agentCommissionList || [];

  const matchesFilter = (a) => {
    if (isReferralAgentRow(a)) return false;
    if (agentKeys == null || agentKeys === undefined) return true;
    if (agentKeys.size === 0) return false;
    const k = String(a.agentId ?? a.agentName ?? "");
    return agentKeys.has(k);
  };

  if (classification === "CO-OPERATING SIDE") {
    let selling = 0;
    for (const a of list) {
      if (!matchesFilter(a)) continue;
      if (isBuyerRebateIncludedYes(a) && a.buyerRebateAmount) {
        selling += parseFloat(a.buyerRebateAmount || 0);
      }
    }
    return { listing: 0, selling: roundTo2(selling) };
  }

  if (classification === "AGENT DOUBLE ENDER") {
    const byAgent = new Map();
    for (const a of list) {
      if (!matchesFilter(a)) continue;
      if (!isAgentDoubleEnderCommissionRow(a)) continue;
      if (!isBuyerRebateIncludedYes(a) || !a.buyerRebateAmount) continue;
      const key = String(a.agentId ?? a.agentName ?? "");
      const amt = parseFloat(a.buyerRebateAmount || 0) || 0;
      const isSelling = (a.transactionType || "").toUpperCase() === "SELLING";
      if (!byAgent.has(key)) {
        byAgent.set(key, { amt, isSelling });
      } else if (isSelling) {
        byAgent.set(key, { amt, isSelling: true });
      }
    }
    let listing = 0;
    let selling = 0;
    byAgent.forEach(({ amt, isSelling }) => {
      if (isSelling) selling += amt;
      else listing += amt;
    });
    for (const a of list) {
      if (!matchesFilter(a)) continue;
      if (isReferralAgentRow(a)) continue;
      if (isAgentDoubleEnderCommissionRow(a)) continue;
      if (isBuyerRebateIncludedYes(a) && a.buyerRebateAmount) {
        listing += parseFloat(a.buyerRebateAmount || 0);
      }
    }
    return { listing: roundTo2(listing), selling: roundTo2(selling) };
  }

  let listing = 0;
  for (const a of list) {
    if (!matchesFilter(a)) continue;
    if (isReferralAgentRow(a)) continue;
    if (isBuyerRebateIncludedYes(a) && a.buyerRebateAmount) {
      listing += parseFloat(a.buyerRebateAmount || 0);
    }
  }
  return { listing: roundTo2(listing), selling: 0 };
}

/**
 * Compute Net to Office total for a trade (same logic as Trade Details Sheet).
 * Invariant: Net to Office = Base Office Commission - Agents Total (minus Liabilities when applicable).
 * Used for Our Brokerage Commission EFT amount so it matches the trade details sheet.
 * @param {Object} trade - Full trade object with commission, keyInfo, agentCommissionList, outsideBrokers
 * @returns {{ netToOfficeTotal: string, netToOfficeSubTotal?: string, netToOfficeHST?: string }}
 */
export function getNetToOfficeTotal(trade) {
  if (!trade) return { netToOfficeTotal: "0.00" };

  const commissionIncome = trade.commission?.commissionIncomeRows?.[0] || {};
  const tradeClassification = trade.keyInfo?.classification || "";
  const isCoOperatingSide = tradeClassification === "CO-OPERATING SIDE";
  const taxRate = getTradeTaxRate(trade);

  const listingAmount =
    commissionIncome.listingAmount && commissionIncome.listingAmount !== ""
      ? parseFloat(commissionIncome.listingAmount)
      : 0;
  const sellingAmount =
    commissionIncome.sellingAmount && commissionIncome.sellingAmount !== ""
      ? parseFloat(commissionIncome.sellingAmount)
      : 0;
  const subTotal = roundTo2Str(listingAmount + sellingAmount);
  const listingTax = roundTo2Str(roundTo2(listingAmount * taxRate));
  const sellingTax = roundTo2Str(roundTo2(sellingAmount * taxRate));
  const hst = roundTo2Str(
    roundTo2(parseFloat(listingTax) + parseFloat(sellingTax)),
  );
  const total = roundTo2Str(roundTo2(parseFloat(subTotal) + parseFloat(hst)));

  let outsideBrokerInfo = null;
  if (trade.commission?.outsideBrokersRows?.length > 0) {
    outsideBrokerInfo = trade.commission.outsideBrokersRows[0];
  } else if (trade.outsideBrokers?.length > 0) {
    outsideBrokerInfo = trade.outsideBrokers[0];
  }

  let obListing = "0.00",
    obSelling = "0.00",
    obSubTotal = "0.00",
    obTax = "0.00",
    obTotal = "0.00";
  if (outsideBrokerInfo) {
    const brokerSellingAmount = outsideBrokerInfo.sellingAmount || "0.00";
    const matchingBrokerInOB = trade.outsideBrokers?.find((ob) => {
      const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
      return (
        outsideBrokerName ===
          (outsideBrokerInfo.agentName || outsideBrokerInfo.firstName) ||
        ob.company === outsideBrokerInfo.company
      );
    });
    let endType = "";
    if (matchingBrokerInOB) {
      endType =
        matchingBrokerInOB.end?.toLowerCase().includes("listing") ||
        matchingBrokerInOB.type?.toLowerCase().includes("listing")
          ? "listing"
          : "selling";
    } else {
      const end = (
        outsideBrokerInfo.end ||
        outsideBrokerInfo.type ||
        ""
      ).toLowerCase();
      endType = end.includes("listing") ? "listing" : "selling";
    }
    if (endType === "listing") {
      obListing = brokerSellingAmount;
      obSelling = "0.00";
    } else {
      obListing = "0.00";
      obSelling = brokerSellingAmount;
    }
    obSubTotal = brokerSellingAmount;
    obTax = outsideBrokerInfo.tax || "0.00";
    obTotal = outsideBrokerInfo.total || "0.00";
  }

  // Agent double ender: both sides are in-house; do not treat cooperating broker row as an expense on the sheet
  if (tradeClassification === "AGENT DOUBLE ENDER") {
    obListing = "0.00";
    obSelling = "0.00";
    obSubTotal = "0.00";
    obTax = "0.00";
    obTotal = "0.00";
  }

  const referralForExpenses = (trade.agentCommissionList || []).filter(
    (a) => (a.classification || "").toUpperCase() === "REFERRAL",
  );
  const referralAmountSum = referralForExpenses.reduce(
    (s, a) => s + parseFloat(a.amount || 0),
    0,
  );
  const referralTaxSum = referralForExpenses.reduce(
    (s, a) => s + parseFloat(a.tax || 0),
    0,
  );
  const referralTotalSum = referralForExpenses.reduce(
    (s, a) => s + parseFloat(a.total || 0),
    0,
  );

  let baseOfficeCommissionListing,
    baseOfficeCommissionSelling,
    baseOfficeCommissionSubTotal,
    baseOfficeCommissionHST,
    baseOfficeCommissionTotal;

  if (isCoOperatingSide) {
    if (referralForExpenses.length === 0) {
      baseOfficeCommissionListing = roundTo2Str(listingAmount);
      baseOfficeCommissionSelling = roundTo2Str(sellingAmount);
      baseOfficeCommissionSubTotal = subTotal;
      baseOfficeCommissionHST = hst;
      baseOfficeCommissionTotal = total;
    } else {
      baseOfficeCommissionListing = roundTo2Str(listingAmount);
      baseOfficeCommissionSelling = roundTo2Str(
        roundTo2(parseFloat(sellingAmount) - referralAmountSum),
      );
      baseOfficeCommissionSubTotal = roundTo2Str(
        roundTo2(parseFloat(subTotal) - referralAmountSum),
      );
      baseOfficeCommissionHST = roundTo2Str(
        roundTo2(parseFloat(hst) - referralTaxSum),
      );
      baseOfficeCommissionTotal = roundTo2Str(
        roundTo2(parseFloat(total) - referralTotalSum),
      );
    }
  } else {
    baseOfficeCommissionListing = roundTo2Str(
      roundTo2(parseFloat(listingAmount) - parseFloat(obListing)),
    );
    baseOfficeCommissionSelling = roundTo2Str(
      roundTo2(
        parseFloat(sellingAmount) - parseFloat(obSelling) - referralAmountSum,
      ),
    );
    baseOfficeCommissionSubTotal = roundTo2Str(
      roundTo2(
        parseFloat(subTotal) - parseFloat(obSubTotal) - referralAmountSum,
      ),
    );
    baseOfficeCommissionHST = roundTo2Str(
      roundTo2(parseFloat(hst) - parseFloat(obTax) - referralTaxSum),
    );
    baseOfficeCommissionTotal = roundTo2Str(
      roundTo2(parseFloat(total) - parseFloat(obTotal) - referralTotalSum),
    );
  }

  const agentDoubleEnderOrder = new Map();
  if (tradeClassification === "AGENT DOUBLE ENDER") {
    (trade.agentCommissionList || []).forEach((a, index) => {
      if (a.classification === "AGENT DOUBLE ENDER" && a.agentId) {
        const key = a.agentId;
        if (!agentDoubleEnderOrder.has(key)) {
          agentDoubleEnderOrder.set(key, []);
        }
        agentDoubleEnderOrder.get(key).push(index);
      }
    });
  }

  const agentCalculations = (trade.agentCommissionList || []).map(
    (agent, agentIndex) => {
      const agentAmount =
        agent.amount && agent.amount !== "" ? parseFloat(agent.amount) : 0;
      const agentFeesDeducted =
        agent.feesDeducted && agent.feesDeducted !== ""
          ? parseFloat(agent.feesDeducted)
          : 0;
      const buyerRebateAmount =
        isBuyerRebateIncludedYes(agent) && agent.buyerRebateAmount
          ? parseFloat(agent.buyerRebateAmount) || 0
          : 0;
      const shareAfterPlan = agentAmount - agentFeesDeducted;
      const displaySubForSheet = Math.max(0, shareAfterPlan - buyerRebateAmount);
      const hstBaseForAgent = shareAfterPlan;

      let agentListing = "0.00";
      let agentSelling = "0.00";

      if (
        tradeClassification === "AGENT DOUBLE ENDER" &&
        agent.classification === "AGENT DOUBLE ENDER"
      ) {
        let transactionType = agent.transactionType;
        if (!transactionType && agent.agentId) {
          const agentIndices = agentDoubleEnderOrder.get(agent.agentId) || [];
          const positionInList = agentIndices.indexOf(agentIndex);
          transactionType = positionInList === 0 ? "LISTING" : "SELLING";
        }
        if (transactionType === "LISTING") {
          agentListing = roundTo2Str(displaySubForSheet);
          agentSelling = "0.00";
        } else if (transactionType === "SELLING") {
          agentListing = "0.00";
          agentSelling = roundTo2Str(displaySubForSheet);
        }
      } else if (isCoOperatingSide) {
        agentListing = "0.00";
        agentSelling = roundTo2Str(displaySubForSheet);
      } else {
        agentListing = roundTo2Str(displaySubForSheet);
        agentSelling = "0.00";
      }

      const agentSubTotal = roundTo2Str(
        roundTo2(parseFloat(agentListing) + parseFloat(agentSelling)),
      );
      const isTaxOverridden =
        agent.taxOverridden === true || agent.taxOverridden === "true";
      const defaultAgentHST = roundTo2(hstBaseForAgent * taxRate);
      const overriddenAgentHST = roundTo2(parseFloat(agent.tax || 0));
      const agentHST = roundTo2Str(
        isTaxOverridden ? overriddenAgentHST : defaultAgentHST,
      );
      const agentTotal = roundTo2Str(
        roundTo2(parseFloat(agentSubTotal) + parseFloat(agentHST)),
      );

      return {
        ...agent,
        agentListing,
        agentSelling,
        agentSubTotal,
        agentHST,
        agentTotal,
      };
    },
  );

  const brCols = getBuyerRebateListingSellingForSheet(trade, {
    agentKeys: null,
  });
  let buyerRebateListing = roundTo2Str(brCols.listing);
  let buyerRebateSelling = roundTo2Str(brCols.selling);
  const totalBuyerRebate = roundTo2(brCols.listing + brCols.selling);

  // For Net to Office we only subtract non-referral agents (referral is already in Base Office Commission)
  const nonReferralAgents = agentCalculations.filter(
    (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
  );

  let totalAgentListing,
    totalAgentSelling,
    totalAgentSubTotal,
    totalAgentHST,
    totalAgentTotal;

  if (!isCoOperatingSide && nonReferralAgents.length === 2) {
    const agent1 = nonReferralAgents[0] || {};
    const agent2 = nonReferralAgents[1] || {};
    totalAgentListing =
      parseFloat(agent1.agentListing || 0) -
      parseFloat(agent2.agentListing || 0);
    totalAgentSelling =
      parseFloat(agent1.agentSelling || 0) -
      parseFloat(agent2.agentSelling || 0);
    totalAgentSubTotal =
      parseFloat(agent1.agentSubTotal || 0) -
      parseFloat(agent2.agentSubTotal || 0);
    totalAgentHST =
      parseFloat(agent1.agentHST || 0) - parseFloat(agent2.agentHST || 0);
    totalAgentTotal =
      parseFloat(agent1.agentTotal || 0) - parseFloat(agent2.agentTotal || 0);
  } else {
    totalAgentListing = nonReferralAgents.reduce(
      (sum, agent) => sum + parseFloat(agent.agentListing || 0),
      0,
    );
    totalAgentSelling = nonReferralAgents.reduce(
      (sum, agent) => sum + parseFloat(agent.agentSelling || 0),
      0,
    );
    totalAgentSubTotal = nonReferralAgents.reduce(
      (sum, agent) => sum + parseFloat(agent.agentSubTotal || 0),
      0,
    );
    totalAgentHST = nonReferralAgents.reduce(
      (sum, agent) => sum + parseFloat(agent.agentHST || 0),
      0,
    );
    totalAgentTotal = nonReferralAgents.reduce(
      (sum, agent) => sum + parseFloat(agent.agentTotal || 0),
      0,
    );
  }

  let netToOfficeTotal;
  let netToOfficeSubTotal;
  let netToOfficeHST;
  let netToOfficeListing;
  let netToOfficeSelling;

  if (tradeClassification === "AGENT DOUBLE ENDER") {
    // Net to Office = Income − agents; buyer rebate is not deducted here (Liabilities row is informational on sheet).
    const sumAgentListing = nonReferralAgents.reduce(
      (s, a) => s + parseFloat(a.agentListing || 0),
      0,
    );
    const sumAgentSelling = nonReferralAgents.reduce(
      (s, a) => s + parseFloat(a.agentSelling || 0),
      0,
    );
    const sumAgentSubTotal = nonReferralAgents.reduce(
      (s, a) => s + parseFloat(a.agentSubTotal || 0),
      0,
    );
    const sumAgentHST = nonReferralAgents.reduce(
      (s, a) => s + parseFloat(a.agentHST || 0),
      0,
    );
    const sumAgentTotal = nonReferralAgents.reduce(
      (s, a) => s + parseFloat(a.agentTotal || 0),
      0,
    );
    const incomeSub = parseFloat(subTotal);
    const incomeHst = parseFloat(hst);
    const incomeTtl = parseFloat(total);
    const officeExpTotals = sumAgentOfficeExpenses(nonReferralAgents);
    const officeExpCols = sumAgentOfficeExpensesByColumn(
      nonReferralAgents,
      isCoOperatingSide,
    );

    netToOfficeListing = roundTo2Str(
      roundTo2(listingAmount - sumAgentListing - officeExpCols.listing),
    );
    netToOfficeSelling = roundTo2Str(
      roundTo2(sellingAmount - sumAgentSelling - officeExpCols.selling),
    );
    netToOfficeSubTotal = roundTo2Str(
      roundTo2(incomeSub - sumAgentSubTotal - officeExpTotals.subTotal),
    );
    netToOfficeHST = roundTo2Str(
      roundTo2(incomeHst - sumAgentHST - officeExpTotals.taxTotal),
    );
    netToOfficeTotal = roundTo2Str(
      roundTo2(incomeTtl - sumAgentTotal - officeExpTotals.total),
    );
  } else if (!isCoOperatingSide && nonReferralAgents.length === 2) {
    // Listing-side two-agent plan: sub-total is plan fees; standard commission HST (Amount × rate) for override rows goes to office HST column.
    const totalPlanFees = nonReferralAgents.reduce((tot, agent) => {
      const officeExp = getAgentOfficeExpenseAmounts(agent);
      return (
        tot +
        parseFloat(agent.feesDeducted || 0) +
        officeExp.subTotal
      );
    }, 0);
    const officeExpenseTaxTotal = nonReferralAgents.reduce(
      (tot, agent) => tot + getAgentOfficeExpenseAmounts(agent).taxTotal,
      0,
    );
    const overriddenHSTToOffice = nonReferralAgents.reduce((tot, agent) => {
      const rowOverride =
        agent.taxOverridden === true || agent.taxOverridden === "true";
      return (
        tot +
        (rowOverride
          ? roundTo2(parseFloat(agent.amount || 0) * taxRate)
          : 0)
      );
    }, 0);
    netToOfficeSubTotal = roundTo2Str(totalPlanFees);
    netToOfficeHST = roundTo2Str(
      roundTo2(overriddenHSTToOffice + officeExpenseTaxTotal),
    );
    netToOfficeTotal = roundTo2Str(
      roundTo2(totalPlanFees + overriddenHSTToOffice + officeExpenseTaxTotal),
    );
  } else {
    const officeExpTotals = sumAgentOfficeExpenses(nonReferralAgents);
    const officeExpCols = sumAgentOfficeExpensesByColumn(
      nonReferralAgents,
      isCoOperatingSide,
    );
    const buyerRebateSub =
      parseFloat(buyerRebateListing) + parseFloat(buyerRebateSelling);
    // Net to Office = base − agent gross − office expenses (shown under agents) − buyer rebate
    netToOfficeListing = roundTo2Str(
      roundTo2(
        parseFloat(baseOfficeCommissionListing) -
          totalAgentListing -
          officeExpCols.listing -
          parseFloat(buyerRebateListing),
      ),
    );
    netToOfficeSelling = roundTo2Str(
      roundTo2(
        parseFloat(baseOfficeCommissionSelling) -
          totalAgentSelling -
          officeExpCols.selling -
          parseFloat(buyerRebateSelling),
      ),
    );
    netToOfficeSubTotal = roundTo2Str(
      roundTo2(
        parseFloat(baseOfficeCommissionSubTotal) -
          totalAgentSubTotal -
          officeExpTotals.subTotal -
          buyerRebateSub,
      ),
    );
    netToOfficeHST = roundTo2Str(
      roundTo2(
        parseFloat(baseOfficeCommissionHST) -
          totalAgentHST -
          officeExpTotals.taxTotal,
      ),
    );
    netToOfficeTotal = roundTo2Str(
      roundTo2(
        parseFloat(baseOfficeCommissionTotal) -
          totalAgentTotal -
          officeExpTotals.total -
          buyerRebateSub,
      ),
    );
  }

  return {
    netToOfficeTotal: netToOfficeTotal || "0.00",
    netToOfficeSubTotal,
    netToOfficeHST,
  };
}

/**
 * EFT amount for Our Brokerage commission: Net to Office total (same as Trade Details sheet)
 * plus buyer rebates. Rebates stay on the sheet as Liabilities but must still move with the
 * office transfer from commission trust (stub: "includes buyer rebates").
 */
export function getOurBrokerageEftAmount(trade) {
  const { netToOfficeTotal } = getNetToOfficeTotal(trade);
  const net = parseFloat(netToOfficeTotal) || 0;
  const rebate = getTotalBuyerRebateAmount(trade);
  return roundTo2Str(roundTo2(net + rebate));
}
